import { BankOutlined, CheckOutlined, SendOutlined, StopOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { formatDate, orDash } from '../../lib/format';
import {
  CHECKLIST_ITEM_LABEL,
  FINANCING_STATUS_LABEL,
  checklistItemTone,
  financingTone,
  isSlow,
} from './financing-status';
import {
  createFinancing,
  decideFinancing,
  getFinancing,
  listChecklistTemplates,
  listFinancing,
  listProviders,
  submitFinancing,
  updateChecklistItem,
  withdrawFinancing,
  type ChecklistItem,
  type ChecklistItemStatus,
  type FinancingApplication,
} from './financing-api';
import { formatMoney } from '../../lib/money';

const { Text } = Typography;

export interface FinancingPanelProps {
  bookingUid: string;
  /** Disabled once the booking is cancelled — there is nothing left to finance. */
  canApply: boolean;
}

/**
 * The financing tab on a booking.
 *
 * Lives here rather than on its own screen because the advisor asking "where is
 * this loan?" is already looking at the booking. A separate destination would
 * mean navigating away from the customer to find out about the customer.
 */
export function FinancingPanel({ bookingUid, canApply }: FinancingPanelProps): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [applying, setApplying] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);

  const [providerUid, setProviderUid] = useState<string | undefined>(undefined);
  const [templateCode, setTemplateCode] = useState<string | undefined>(undefined);
  const [tenureMonths, setTenureMonths] = useState(60);
  const [amount, setAmount] = useState('');
  const [providerRefNo, setProviderRefNo] = useState('');
  const [decision, setDecision] = useState<'APPROVED' | 'REJECTED'>('APPROVED');
  const [reason, setReason] = useState('');
  const [itemStatus, setItemStatus] = useState<ChecklistItemStatus>('WAIVED');
  const [itemNote, setItemNote] = useState('');

  const { data: page, isLoading } = useQuery({
    queryKey: ['financing', 'booking', bookingUid],
    queryFn: () => listFinancing({ bookingUid }),
  });

  // The live one, if any. Everything else is history the next advisor reads.
  const applications = page?.items ?? [];
  const live = applications.find((a) => a.status === 'PENDING' || a.status === 'SUBMITTED');
  const past = applications.filter((a) => a !== live);

  const { data: detail } = useQuery({
    queryKey: ['financing', live?.uid],
    queryFn: () => getFinancing(live?.uid ?? ''),
    enabled: live !== undefined,
  });

  const { data: providers } = useQuery({
    queryKey: ['financing', 'providers'],
    queryFn: listProviders,
    enabled: applying,
  });
  const { data: templates } = useQuery({
    queryKey: ['financing', 'templates'],
    queryFn: listChecklistTemplates,
    enabled: applying,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['financing'] });
    void queryClient.invalidateQueries({ queryKey: ['booking', bookingUid] });
  };
  const fail = (e: unknown, fallback: string): void => {
    void message.error(e instanceof ApiError ? e.message : fallback);
  };

  const apply = useMutation({
    mutationFn: () =>
      createFinancing({
        bookingUid,
        providerUid: providerUid ?? '',
        templateCode: templateCode ?? '',
        tenureMonths,
        ...(amount.trim().length > 0 ? { amount: amount.trim() } : {}),
      }),
    onSuccess: () => {
      refresh();
      setApplying(false);
      void message.success(t('financing.created'));
    },
    onError: (e: unknown) => {
      fail(e, t('financing.createFailed'));
    },
  });

  const submit = useMutation({
    mutationFn: () =>
      submitFinancing(live?.uid ?? '', {
        ...(providerRefNo.trim().length > 0 ? { providerRefNo: providerRefNo.trim() } : {}),
      }),
    onSuccess: () => {
      refresh();
      void message.success(t('financing.submitted'));
    },
    // The gate's refusal names the missing documents; showing it verbatim is
    // the whole point of the API bothering to list them.
    onError: (e: unknown) => {
      fail(e, t('financing.submitFailed'));
    },
  });

  const decide = useMutation({
    mutationFn: () =>
      decideFinancing(live?.uid ?? '', {
        status: decision,
        ...(reason.trim().length > 0 ? { reason: reason.trim() } : {}),
      }),
    onSuccess: (result) => {
      refresh();
      setDeciding(false);
      void message.success(result.status === 'APPROVED' ? t('financing.approved') : t('financing.rejected'));
    },
    onError: (e: unknown) => {
      fail(e, t('financing.decideFailed'));
    },
  });

  const withdraw = useMutation({
    mutationFn: () => withdrawFinancing(live?.uid ?? '', { reason: reason.trim() }),
    onSuccess: () => {
      refresh();
      setWithdrawing(false);
      void message.success(t('financing.withdrawn'));
    },
    onError: (e: unknown) => {
      fail(e, t('financing.withdrawFailed'));
    },
  });

  const updateItem = useMutation({
    mutationFn: () =>
      updateChecklistItem(editingItem?.uid ?? '', {
        status: itemStatus,
        ...(itemNote.trim().length > 0 ? { note: itemNote.trim() } : {}),
      }),
    onSuccess: () => {
      refresh();
      setEditingItem(null);
    },
    onError: (e: unknown) => {
      fail(e, t('financing.itemFailed'));
    },
  });

  if (isLoading) return null;

  if (live === undefined && past.length === 0) {
    return (
      <Card>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('financing.none')}>
          {canApply && (
            <Button
              type="primary"
              icon={<BankOutlined />}
              onClick={() => {
                setApplying(true);
              }}
            >
              {t('financing.apply')}
            </Button>
          )}
        </Empty>
        {applyDrawer()}
      </Card>
    );
  }

  const checklist = detail?.checklist ?? null;
  const needsNote = itemStatus === 'WAIVED' || itemStatus === 'NOT_APPLICABLE';

  return (
    <Flex vertical gap={16}>
      {live !== undefined && (
        <>
          {live.status === 'PENDING' && checklist !== null && !checklist.isComplete && (
            <Alert
              type="warning"
              showIcon
              message={t('financing.stillNeeded', { count: checklist.outstanding.length })}
              description={checklist.outstanding.join(' · ')}
            />
          )}
          {isSlow(live.daysAwaitingDecision) && (
            <Alert
              type="error"
              showIcon
              message={t('financing.slow', {
                days: live.daysAwaitingDecision,
                provider: live.providerName,
              })}
              description={t('financing.slowHelp', {
                phone: live.officerPhone ?? t('financing.noOfficerPhone'),
              })}
            />
          )}

          <Card
            title={
              <Flex align="center" gap={8} wrap>
                <Text strong>{live.providerName}</Text>
                <Tag color={financingTone(live.status)}>{FINANCING_STATUS_LABEL[live.status]}</Tag>
              </Flex>
            }
            extra={
              <Space wrap>
                {live.status === 'PENDING' && (
                  <>
                    <Input
                      value={providerRefNo}
                      onChange={(e) => {
                        setProviderRefNo(e.target.value);
                      }}
                      placeholder={t('financing.refPlaceholder')}
                      style={{ width: 200 }}
                    />
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      loading={submit.isPending}
                      disabled={checklist !== null && !checklist.isComplete}
                      onClick={() => {
                        submit.mutate();
                      }}
                    >
                      {t('financing.submit')}
                    </Button>
                  </>
                )}
                {live.status === 'SUBMITTED' && (
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={() => {
                      setDecision('APPROVED');
                      setReason('');
                      setDeciding(true);
                    }}
                  >
                    {t('financing.decide')}
                  </Button>
                )}
                <Button
                  danger
                  icon={<StopOutlined />}
                  onClick={() => {
                    setReason('');
                    setWithdrawing(true);
                  }}
                >
                  {t('financing.withdraw')}
                </Button>
              </Space>
            }
          >
            <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
              <Descriptions.Item label={t('financing.fields.applicationNo')}>
                {live.applicationNo}
              </Descriptions.Item>
              <Descriptions.Item label={t('financing.fields.providerRefNo')}>
                {orDash(live.providerRefNo)}
              </Descriptions.Item>
              <Descriptions.Item label={t('financing.fields.amount')}>
                <Text strong>{formatMoney(live.amount, live.currency)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('financing.fields.tenure')}>
                {t('financing.months', { count: live.tenureMonths })}
              </Descriptions.Item>
              <Descriptions.Item label={t('financing.fields.indicative')} span={2}>
                <Flex vertical gap={2}>
                  <Text>{formatMoney(live.indicativeMonthly, live.currency)}</Text>
                  {/* Never present this as a quote. See monthlyInstalment(). */}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('financing.indicativeHelp')}
                  </Text>
                </Flex>
              </Descriptions.Item>
              <Descriptions.Item label={t('financing.fields.submittedAt')}>
                {live.submittedAt === null ? '—' : formatDate(live.submittedAt)}
              </Descriptions.Item>
              <Descriptions.Item label={t('financing.fields.waiting')}>
                {live.daysAwaitingDecision === null ? (
                  '—'
                ) : (
                  <Text type={isSlow(live.daysAwaitingDecision) ? 'danger' : undefined}>
                    {t('financing.days', { count: live.daysAwaitingDecision })}
                  </Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {checklist !== null && (
            <Card title={checklist.label} styles={{ body: { padding: 0 } }}>
              <Table<ChecklistItem>
                dataSource={checklist.items}
                rowKey={(row) => row.uid}
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
                columns={[
                  {
                    title: t('financing.fields.document'),
                    dataIndex: 'label',
                    render: (label: string, row: ChecklistItem) => (
                      <Flex vertical gap={2}>
                        <Text>{label}</Text>
                        {row.note !== null && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {row.note}
                          </Text>
                        )}
                      </Flex>
                    ),
                  },
                  {
                    title: t('financing.fields.required'),
                    dataIndex: 'isMandatory',
                    width: 110,
                    render: (mandatory: boolean) =>
                      mandatory ? (
                        <Text>{t('financing.required')}</Text>
                      ) : (
                        <Text type="secondary">{t('financing.optional')}</Text>
                      ),
                  },
                  {
                    title: t('sales.fields.status'),
                    dataIndex: 'status',
                    width: 150,
                    render: (status: ChecklistItemStatus) => (
                      <Tag color={checklistItemTone(status)}>{CHECKLIST_ITEM_LABEL[status]}</Tag>
                    ),
                  },
                  {
                    title: '',
                    width: 100,
                    render: (_: unknown, row: ChecklistItem) =>
                      live.status === 'PENDING' ? (
                        <Button
                          size="small"
                          onClick={() => {
                            setItemStatus(row.status === 'PENDING' ? 'WAIVED' : row.status);
                            setItemNote(row.note ?? '');
                            setEditingItem(row);
                          }}
                        >
                          {t('financing.updateItem')}
                        </Button>
                      ) : null,
                  },
                ]}
              />
            </Card>
          )}
        </>
      )}

      {live === undefined && canApply && (
        <Card>
          <Flex vertical gap={12} align="flex-start">
            <Text type="secondary">{t('financing.noneLive')}</Text>
            <Button
              type="primary"
              icon={<BankOutlined />}
              onClick={() => {
                setApplying(true);
              }}
            >
              {t('financing.applyAnother')}
            </Button>
          </Flex>
        </Card>
      )}

      {past.length > 0 && (
        <Card title={t('financing.previous')} styles={{ body: { padding: 0 } }}>
          <Table<FinancingApplication>
            dataSource={past}
            rowKey={(row) => row.uid}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            columns={[
              { title: t('financing.fields.provider'), dataIndex: 'providerName', width: 200 },
              {
                title: t('sales.fields.status'),
                dataIndex: 'status',
                width: 140,
                render: (status: FinancingApplication['status']) => (
                  <Tag color={financingTone(status)}>{FINANCING_STATUS_LABEL[status]}</Tag>
                ),
              },
              {
                // The single most useful column on this table: the next
                // application to another bank is written from it.
                title: t('financing.fields.why'),
                render: (_: unknown, row: FinancingApplication) =>
                  orDash(row.declineReason ?? row.withdrawReason),
              },
              {
                title: t('financing.fields.decidedAt'),
                dataIndex: 'decidedAt',
                width: 140,
                render: (value: string | null) => (value === null ? '—' : formatDate(value)),
              },
            ]}
          />
        </Card>
      )}

      {applyDrawer()}

      <ActionDrawer
        open={deciding}
        title={t('financing.decideTitle')}
        submitLabel={t('financing.decideSubmit')}
        submitting={decide.isPending}
        submitDisabled={decision === 'REJECTED' && reason.trim().length < 3}
        onClose={() => {
          setDeciding(false);
        }}
        onSubmit={() => {
          decide.mutate();
        }}
        consequence={t(
          decision === 'APPROVED' ? 'financing.approveConsequence' : 'financing.rejectConsequence',
          { name: live?.customerName ?? '', provider: live?.providerName ?? '' },
        )}
      >
        <Form layout="vertical">
          <Form.Item label={t('financing.fields.decision')} required>
            <Radio.Group
              value={decision}
              onChange={(e) => {
                setDecision(e.target.value as 'APPROVED' | 'REJECTED');
              }}
              optionType="button"
              buttonStyle="solid"
              size="large"
              options={[
                { value: 'APPROVED', label: t('financing.approvedShort') },
                { value: 'REJECTED', label: t('financing.rejectedShort') },
              ]}
            />
          </Form.Item>
          <Form.Item
            label={t('financing.fields.why')}
            required={decision === 'REJECTED'}
            help={decision === 'REJECTED' ? t('financing.rejectReasonHelp') : undefined}
          >
            <Input.TextArea
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              placeholder={t('financing.rejectReasonPlaceholder')}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={withdrawing}
        danger
        title={t('financing.withdrawTitle')}
        submitLabel={t('financing.withdrawSubmit')}
        submitting={withdraw.isPending}
        submitDisabled={reason.trim().length < 3}
        onClose={() => {
          setWithdrawing(false);
        }}
        onSubmit={() => {
          withdraw.mutate();
        }}
        consequence={t('financing.withdrawConsequence', { provider: live?.providerName ?? '' })}
      >
        <Form layout="vertical">
          <Form.Item label={t('financing.fields.why')} required>
            <Input.TextArea
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              placeholder={t('financing.withdrawReasonPlaceholder')}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={editingItem !== null}
        title={editingItem?.label ?? ''}
        submitLabel={t('common.save')}
        submitting={updateItem.isPending}
        submitDisabled={needsNote && itemNote.trim().length < 3}
        onClose={() => {
          setEditingItem(null);
        }}
        onSubmit={() => {
          updateItem.mutate();
        }}
      >
        <Form layout="vertical">
          <Form.Item label={t('sales.fields.status')} required>
            <Radio.Group
              value={itemStatus}
              onChange={(e) => {
                setItemStatus(e.target.value as ChecklistItemStatus);
              }}
              options={[
                // RECEIVED is deliberately absent: the API refuses it without an
                // attachment, and uploads arrive with the attachments UI. An
                // option that always fails is worse than no option.
                { value: 'WAIVED', label: t('financing.waived') },
                { value: 'NOT_APPLICABLE', label: t('financing.notApplicable') },
                { value: 'PENDING', label: t('financing.stillWaiting') },
              ]}
            />
          </Form.Item>
          <Form.Item
            label={t('financing.fields.note')}
            required={needsNote}
            help={needsNote ? t('financing.noteHelp') : undefined}
          >
            <Input.TextArea
              rows={2}
              value={itemNote}
              onChange={(e) => {
                setItemNote(e.target.value);
              }}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>
    </Flex>
  );

  function applyDrawer(): ReactNode {
    return (
      <ActionDrawer
        open={applying}
        title={t('financing.applyTitle')}
        submitLabel={t('financing.applySubmit')}
        submitting={apply.isPending}
        submitDisabled={providerUid === undefined || templateCode === undefined}
        onClose={() => {
          setApplying(false);
        }}
        onSubmit={() => {
          apply.mutate();
        }}
      >
        <Form layout="vertical">
          <Form.Item label={t('financing.fields.provider')} required>
            <Select
              showSearch
              value={providerUid}
              onChange={setProviderUid}
              optionFilterProp="label"
              placeholder={t('financing.providerPlaceholder')}
              options={(providers ?? []).map((p) => ({ value: p.uid, label: p.name }))}
            />
          </Form.Item>
          <Form.Item label={t('financing.fields.template')} required help={t('financing.templateHelp')}>
            <Select
              value={templateCode}
              onChange={setTemplateCode}
              placeholder={t('financing.templatePlaceholder')}
              options={(templates ?? []).map((tpl) => ({ value: tpl.code, label: tpl.label }))}
            />
          </Form.Item>
          <Form.Item label={t('financing.fields.tenure')} required>
            <InputNumber
              min={6}
              max={108}
              value={tenureMonths}
              onChange={(value) => {
                setTenureMonths(value ?? 60);
              }}
              suffix={t('financing.monthsUnit')}
              style={{ width: '100%' }}
              size="large"
            />
          </Form.Item>
          <Form.Item label={t('financing.fields.amount')} help={t('financing.amountHelp')}>
            <Input
              inputMode="decimal"
              prefix="MYR"
              size="large"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
              }}
              placeholder={t('financing.amountPlaceholder')}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>
    );
  }
}
